/**
 * Integrytas Group - Common Security Module (CSM)
 * use_unsaved_changes — per-workspace-tab unsaved-changes tracking.
 *
 * Any form publishes its dirty state to the shell, keyed by its owning page
 * code, via `useUnsavedChanges`. The Multi-Tab Manager then shows an amber dot
 * in place of that tab's close control and prompts for confirmation before the
 * tab is closed (see TabManagerShell / TabStrip). This is behaviour, not just
 * styling: closing a tab with unsaved edits is guarded.
 *
 * `useDirtyForm` calls this automatically using the page code from the
 * personalization context, so any existing dirty form marks its tab with zero
 * per-page wiring. It no-ops safely outside the shell (default context).
 */

import { createContext, useContext, useEffect, useId } from 'react';

export interface UnsavedChangesApi {
  /**
   * Publish (or clear) one dirty source. A source is identified by a stable id
   * (one per form instance) so several forms can mark the same tab; the tab is
   * dirty while ANY of its sources is. `pageCode === null` or `dirty === false`
   * clears the source.
   */
  setSource: (sourceId: string, pageCode: string | null, dirty: boolean) => void;
}

export const UnsavedChangesContext = createContext<UnsavedChangesApi>({ setSource: () => undefined });

/**
 * Register a form's unsaved state against its owning page (tab). Clears itself
 * on unmount so a closed/saved form stops marking its tab.
 */
export function useUnsavedChanges(pageCode: string | null, isDirty: boolean): void {
  const { setSource } = useContext(UnsavedChangesContext);
  const sourceId = useId();

  useEffect(() => {
    setSource(sourceId, pageCode, isDirty);
  }, [setSource, sourceId, pageCode, isDirty]);

  useEffect(() => () => setSource(sourceId, null, false), [setSource, sourceId]);
}
